import {
  Archive,
  Check,
  Clock,
  Loader2,
  MessageCircle,
  Pencil,
  Pin,
  RefreshCw,
  Search,
  Send,
  SlidersHorizontal,
  Star,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { AdminState } from "./AdminState";
import { StatusBadge } from "./StatusBadge";
import { adminPost, buildAdminQuery, type AdminReview, useAdminQuery } from "./adminApi";

type ReviewsResponse = {
  ok: true;
  reviews: AdminReview[];
  total: number;
};

type ReviewAction = "archive" | "edit" | "feature" | "new-flow" | "publish" | "reject" | "reply";
type ReviewEditDraft = Pick<AdminReview, "body" | "displayName"> & { title: string };

const statusOptions = ["all", "PENDING", "PUBLISHED", "REJECTED", "ARCHIVED"];
const sourceOptions = ["all", "WHATSAPP", "EMAIL", "STOREFRONT_FORM", "QR_CODE", "MANUAL_IMPORT", "ADMIN_CREATED"];
const sortOptions = ["newest", "oldest", "rating-high", "rating-low", "featured"];
const adminTimeZone = "Africa/Cairo";

export function ReviewTable({ focus }: { focus?: string }) {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(focus === "moderation" ? "PENDING" : "all");
  const [source, setSource] = useState("all");
  const [media, setMedia] = useState("all");
  const [rating, setRating] = useState("all");
  const [sort, setSort] = useState("newest");
  const [busyAction, setBusyAction] = useState<string>();
  const [notice, setNotice] = useState<{ tone: "critical" | "success"; text: string }>();
  const [showPolicyNotice, setShowPolicyNotice] = useState(true);
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const query = useMemo(
    () => buildAdminQuery("/api/admin/reviews", { media, rating, search, sort, source, status }),
    [media, rating, search, sort, source, status],
  );
  const reviews = useAdminQuery<ReviewsResponse>(query, (data) => data.reviews.length === 0);
  const visibleReviews = reviews.data?.reviews ?? [];
  const allVisibleSelected = visibleReviews.length > 0 && visibleReviews.every((review) => selectedIds.includes(review.id));
  const averageRating = visibleReviews.length
    ? visibleReviews.reduce((total, review) => total + review.rating, 0) / visibleReviews.length
    : 0;

  async function runAction(review: AdminReview, action: ReviewAction, payload?: ReviewEditDraft) {
    const busyKey = `${review.id}:${action}`;
    setBusyAction(busyKey);
    setNotice(undefined);
    try {
      const body =
        action === "edit"
          ? payload
          : action === "reply"
          ? { reply: replyDrafts[review.id] ?? review.merchantReply ?? "" }
          : action === "feature"
            ? { featured: !review.featured }
            : {};
      await adminPost(`/api/admin/reviews/${review.id}/${action}`, body);
      setNotice({ tone: "success", text: actionNotice(action) });
      setSelectedIds((current) => current.filter((id) => id !== review.id));
      await reviews.reload();
      return true;
    } catch (error) {
      setNotice({
        tone: "critical",
        text: error instanceof Error ? error.message : "Review action failed.",
      });
      return false;
    } finally {
      setBusyAction(undefined);
    }
  }

  function resetFilters() {
    setSearch("");
    setStatus(focus === "moderation" ? "PENDING" : "all");
    setSource("all");
    setMedia("all");
    setRating("all");
    setSort("newest");
  }

  function toggleReview(reviewId: string) {
    setSelectedIds((current) => (current.includes(reviewId) ? current.filter((id) => id !== reviewId) : [...current, reviewId]));
  }

  function toggleAllVisible() {
    setSelectedIds((current) => {
      if (allVisibleSelected) return current.filter((id) => !visibleReviews.some((review) => review.id === id));
      return [...new Set([...current, ...visibleReviews.map((review) => review.id)])];
    });
  }

  return (
    <section className="wr-review-workspace">
      <div className="wr-review-tabs" aria-label="Review type">
        <button type="button" aria-pressed="true">
          Product reviews
        </button>
        <button type="button" aria-pressed="false">
          Store reviews
        </button>
      </div>

      {showPolicyNotice ? (
        <div className="wr-review-alert" role="status">
          <span>i</span>
          <p>
            4 stars and up can be auto-published. Keep pending reviews visible here until you publish, reject, or reply with
            context.
          </p>
          <button type="button" aria-label="Dismiss review policy note" onClick={() => setShowPolicyNotice(false)}>
            <X size={15} aria-hidden />
          </button>
        </div>
      ) : null}

      <div className="wr-review-summary-bar">
        <strong>Total {reviews.data?.total ?? 0} reviews</strong>
        <span aria-hidden="true">|</span>
        <span className="wr-review-stars">{stars(Math.round(averageRating || 0))}</span>
        <strong>{averageRating ? averageRating.toFixed(1) : "0.0"}</strong>
      </div>

      <div className="wr-review-filter-panel">
        <label className="wr-review-search">
          <Search size={18} aria-hidden />
          <input
            aria-label="Search reviews"
            placeholder="Search by email, display name, product, or review text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <SlidersHorizontal size={17} aria-hidden />
        </label>
        <div className="wr-review-filter-row">
          <ReviewSelect label="Status" value={status} onChange={setStatus} options={statusOptions} />
          <ReviewSelect label="Rating" value={rating} onChange={setRating} options={["all", "5", "4", "3", "2", "1"]} />
          <ReviewSelect label="Sort" value={sort} onChange={setSort} options={sortOptions} />
          <ReviewSelect label="Media" value={media} onChange={setMedia} options={["all", "with-media", "without-media"]} />
          <ReviewSelect label="Source" value={source} onChange={setSource} options={sourceOptions} />
          <button className="wr-filter-chip" type="button">
            Add filter +
          </button>
          <button className="wr-filter-clear" type="button" onClick={resetFilters}>
            Clear all
          </button>
          <button className="wr-filter-apply" type="button" onClick={() => void reviews.reload()}>
            Apply
          </button>
        </div>
        <div className="wr-review-list-controls">
          <label>
            <input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible} />
            <span>
              Select all <strong>{reviews.data?.total ?? 0}</strong> reviews
            </span>
          </label>
          <div>
            {selectedIds.length ? <StatusBadge tone="info">{selectedIds.length} selected</StatusBadge> : null}
            <button className="wr-icon-button" type="button" aria-label="Refresh reviews" onClick={() => void reviews.reload()}>
              <RefreshCw size={15} aria-hidden />
            </button>
          </div>
        </div>
      </div>

      {notice ? (
        <div className={`wr-notice wr-notice--${notice.tone}`} role="status">
          {notice.text}
        </div>
      ) : null}

      <AdminState
        status={reviews.status}
        error={reviews.error}
        onRetry={() => void reviews.reload()}
        emptyTitle="No matching reviews"
        emptyText="Change filters or import reviews to start moderation."
      >
        <div className="wr-review-card-list">
          {visibleReviews.map((review) => (
            <ReviewCard
              key={review.id}
              busyAction={busyAction}
              isSelected={selectedIds.includes(review.id)}
              onAction={runAction}
              onReplyChange={(value) => setReplyDrafts((drafts) => ({ ...drafts, [review.id]: value }))}
              onToggleSelected={() => toggleReview(review.id)}
              replyValue={replyDrafts[review.id] ?? review.merchantReply ?? ""}
              review={review}
            />
          ))}
        </div>
      </AdminState>
    </section>
  );
}

function ReviewCard({
  busyAction,
  isSelected,
  onAction,
  onReplyChange,
  onToggleSelected,
  replyValue,
  review,
}: {
  busyAction?: string;
  isSelected: boolean;
  onAction: (review: AdminReview, action: ReviewAction, payload?: ReviewEditDraft) => Promise<boolean>;
  onReplyChange: (value: string) => void;
  onToggleSelected: () => void;
  replyValue: string;
  review: AdminReview;
}) {
  const conversationWindow = conversationWindowLabel(review);
  const customerReplies = review.customerReplies.slice(0, 3);
  const hiddenReplyCount = Math.max(0, review.customerReplies.length - customerReplies.length);
  const [isEditing, setIsEditing] = useState(false);
  const [editDraft, setEditDraft] = useState<ReviewEditDraft>({
    body: review.body,
    displayName: review.displayName,
    title: review.title ?? "",
  });

  async function saveEdit() {
    if (await onAction(review, "edit", editDraft)) setIsEditing(false);
  }

  return (
    <article className={`wr-review-card wr-review-card--${review.rawStatus.toLowerCase()}`}>
      <label className="wr-review-card__check" aria-label={`Select review from ${review.displayName}`}>
        <input type="checkbox" checked={isSelected} onChange={onToggleSelected} />
      </label>
      <div className="wr-review-card__body">
        <div className="wr-review-card__head">
          <div>
            <strong>{review.displayName}</strong>
            <div className="wr-review-product">
              <span>{review.product}</span>
              {review.productHandle ? <small>{review.productHandle}</small> : null}
            </div>
            <div className="wr-review-meta">
              <span className="wr-review-stars" aria-label={`${review.rating} out of 5 stars`}>
                {stars(review.rating)}
              </span>
              <span>{formatShortDate(review.createdAt)}</span>
              <span>{review.source}</span>
              {review.verified ? <span>Verified order</span> : null}
            </div>
          </div>
          <div className="wr-review-card__actions">
            <ActionButton
              busy={busyAction === `${review.id}:feature`}
              icon={review.featured ? <Star size={15} aria-hidden /> : <Pin size={15} aria-hidden />}
              label={review.featured ? "Unfeature" : "Feature"}
              onClick={() => void onAction(review, "feature")}
            />
            <ActionButton
              busy={busyAction === `${review.id}:edit`}
              icon={<Pencil size={15} aria-hidden />}
              label="Edit"
              onClick={() => setIsEditing((value) => !value)}
            />
          </div>
        </div>

        {isEditing ? (
          <form
            className="wr-review-edit-form"
            onSubmit={(event) => {
              event.preventDefault();
              void saveEdit();
            }}
          >
            <label>
              Reviewer name
              <input
                maxLength={80}
                required
                value={editDraft.displayName}
                onChange={(event) => setEditDraft((draft) => ({ ...draft, displayName: event.target.value }))}
              />
            </label>
            <label>
              Review title
              <input
                maxLength={120}
                value={editDraft.title}
                onChange={(event) => setEditDraft((draft) => ({ ...draft, title: event.target.value }))}
              />
            </label>
            <label>
              Review text
              <textarea
                maxLength={5000}
                required
                rows={4}
                value={editDraft.body}
                onChange={(event) => setEditDraft((draft) => ({ ...draft, body: event.target.value }))}
              />
            </label>
            <div>
              <button type="submit" disabled={busyAction === `${review.id}:edit`}>
                {busyAction === `${review.id}:edit` ? <Loader2 className="wr-spin" size={14} aria-hidden /> : "Save changes"}
              </button>
              <button type="button" onClick={() => setIsEditing(false)}>
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="wr-review-copy-block">
            <StatusBadge tone={statusTone(review.rawStatus)}>{review.status}</StatusBadge>
            {review.featured ? <StatusBadge tone="success">Featured</StatusBadge> : null}
            {review.title ? <h3>{review.title}</h3> : null}
            <p>{review.body}</p>
          </div>
        )}

        {review.conversationStep || review.customerReplies.length ? (
          <div className="wr-review-conversation">
            <div>
              <MessageCircle size={15} aria-hidden />
              <strong>{review.conversationStep ? `Conversation: ${review.conversationStep}` : "Customer replies"}</strong>
              {review.lastCustomerReplyAt ? <time dateTime={review.lastCustomerReplyAt}>{formatDate(review.lastCustomerReplyAt)}</time> : null}
              {conversationWindow ? (
                <span className={`wr-review-window ${review.conversationWindowClosed ? "wr-review-window--closed" : ""}`}>
                  <Clock size={13} aria-hidden />
                  {conversationWindow}
                </span>
              ) : null}
            </div>
            {review.customerReplies.length ? (
              <div className="wr-review-conversation__replies">
                {customerReplies.map((reply) => (
                  <blockquote key={`${review.id}:${reply.receivedAt}`}>
                    <p>{reply.text}</p>
                    <time dateTime={reply.receivedAt}>{formatDate(reply.receivedAt)}</time>
                  </blockquote>
                ))}
                {hiddenReplyCount ? <small>+{hiddenReplyCount} older repl{hiddenReplyCount === 1 ? "y" : "ies"}</small> : null}
              </div>
            ) : (
              <p>No customer text, photo, or video has arrived after the rating yet.</p>
            )}
          </div>
        ) : null}

        <div className="wr-review-card__footer">
          <div className="wr-review-status-actions">
            <ActionButton
              busy={busyAction === `${review.id}:publish`}
              icon={<Check size={14} aria-hidden />}
              label="Publish"
              onClick={() => void onAction(review, "publish")}
            />
            <ActionButton
              busy={busyAction === `${review.id}:reject`}
              icon={<X size={14} aria-hidden />}
              label="Reject"
              onClick={() => void onAction(review, "reject")}
            />
            <ActionButton
              busy={busyAction === `${review.id}:archive`}
              icon={<Archive size={14} aria-hidden />}
              label="Archive"
              onClick={() => void onAction(review, "archive")}
            />
            <ActionButton
              busy={busyAction === `${review.id}:new-flow`}
              icon={<Send size={14} aria-hidden />}
              label="New review flow"
              onClick={() => void onAction(review, "new-flow")}
            />
          </div>
        </div>

        <form
          className="wr-review-reply-bar"
          onSubmit={(event) => {
            event.preventDefault();
            void onAction(review, "reply");
          }}
        >
          <MessageCircle size={15} aria-hidden />
          <input
            aria-label={`Merchant reply for ${review.displayName}`}
            placeholder="Add a reply..."
            value={replyValue}
            onChange={(event) => onReplyChange(event.target.value)}
          />
          <button type="submit" disabled={busyAction === `${review.id}:reply`}>
            {busyAction === `${review.id}:reply` ? <Loader2 className="wr-spin" size={14} aria-hidden /> : "Save reply"}
          </button>
        </form>
      </div>
      <aside className="wr-review-media-panel" aria-label="Review media">
        {review.mediaPreviewUrl ? (
          <img src={review.mediaPreviewUrl} alt="" loading="lazy" />
        ) : (
          <span>{review.mediaCount ? "+1" : "No media"}</span>
        )}
        <small>{review.mediaCount ? `${review.mediaCount} media item${review.mediaCount === 1 ? "" : "s"}` : review.media}</small>
      </aside>
    </article>
  );
}

function ReviewSelect({
  label,
  onChange,
  options,
  value,
}: {
  label: string;
  onChange: (value: string) => void;
  options: string[];
  value: string;
}) {
  return (
    <label className="wr-filter-select">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)} aria-label={label}>
        {options.map((option) => (
          <option key={option} value={option}>
            {selectLabel(label, option)}
          </option>
        ))}
      </select>
    </label>
  );
}

function ActionButton({
  busy,
  icon,
  label,
  onClick,
}: {
  busy: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button disabled={busy} type="button" onClick={onClick} aria-label={label} title={label}>
      {busy ? <Loader2 className="wr-spin" size={14} aria-hidden /> : icon}
      <span>{label}</span>
    </button>
  );
}

function selectLabel(label: string, value: string) {
  if (label === "Rating" && value !== "all") return `${value} stars`;
  if (label === "Sort") {
    const sortLabels: Record<string, string> = {
      newest: "Newest first",
      oldest: "Oldest first",
      "rating-high": "Highest rating",
      "rating-low": "Lowest rating",
      featured: "Featured first",
    };
    return sortLabels[value] ?? labelEnum(value);
  }
  if (value === "with-media") return "With media";
  if (value === "without-media") return "No media";
  return value === "all" ? `All ${label.toLowerCase()}` : labelEnum(value);
}

function labelEnum(value: string) {
  return value
    .toLowerCase()
    .split("_")
    .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`)
    .join(" ");
}

function statusTone(status: string) {
  if (status === "PUBLISHED") return "success";
  if (status === "REJECTED" || status === "ARCHIVED") return "critical";
  if (status === "PENDING") return "warning";
  return "neutral";
}

function actionNotice(action: ReviewAction) {
  const labels: Record<ReviewAction, string> = {
    archive: "Review archived.",
    edit: "Review updated.",
    feature: "Featured state updated.",
    "new-flow": "New review flow queued.",
    publish: "Review published and storefront aggregate refreshed.",
    reject: "Review rejected.",
    reply: "Merchant reply saved.",
  };
  return labels[action];
}

function conversationWindowLabel(review: AdminReview) {
  if (!review.conversationWindowEndsAt) return null;
  const closesAt = formatWindowDate(review.conversationWindowEndsAt);
  if (review.conversationWindowClosed) return `Closed ${closesAt}`;
  return `Open until ${closesAt}`;
}

function stars(rating: number) {
  const rounded = Math.max(0, Math.min(5, Math.round(rating)));
  return `${"★".repeat(rounded)}${"☆".repeat(5 - rounded)}`;
}

function formatShortDate(value: string) {
  return new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", timeZone: adminTimeZone }).format(new Date(value));
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    month: "short",
    timeZone: adminTimeZone,
    timeZoneName: "short",
    weekday: "short",
    year: "numeric",
  }).format(new Date(value));
}

function formatWindowDate(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    month: "short",
    timeZone: adminTimeZone,
    timeZoneName: "short",
    weekday: "short",
  }).format(new Date(value));
}
